// src/pages/TermsPage.jsx
// Terms & Conditions — client's own terms, laid out as easy-to-scan cards.
// Minimum order values come from src/config/tenant.js so they stay in sync
// with the Products page and Home page.

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  SHOP_NAME, SHOP_PHONES, ADMIN_EMAIL, ADMIN_WHATSAPP_NUMBER,
  MIN_ORDER_TN, MIN_ORDER_OTHER,
} from '../config/tenant';
import { fmt } from '../components/ui';
import './InfoPages.css';

const TERMS = [
  { icon: '🇮🇳', title: 'All India orders',
    text: 'We accept orders from all over India.' },
  { icon: '🛒', title: 'Minimum order',
    text: `The minimum order amount is ${fmt(MIN_ORDER_TN)} for Tamil Nadu and ${fmt(MIN_ORDER_OTHER)} for other states.` },
  { icon: '💳', title: 'Payment before shipping',
    text: 'Products will be shipped only after we confirm full payment.' },
  { icon: '📦', title: 'Transit damages',
    text: 'We are not responsible for any damages during transportation.' },
  { icon: '🗓️', title: 'Other states — order early',
    text: 'Customers from other states should place orders at least 1 month before Diwali.' },
  { icon: '🚚', title: 'Third-party delivery',
    text: 'Delivery is handled by third-party transport companies.' },
  { icon: '⏰', title: 'Order on time',
    text: 'Orders must be placed at least 10 to 15 days before Diwali.' },
  { icon: '🚫', title: 'Order cancellation',
    text: 'We reserve the right to cancel orders due to late order, non-payment, or inability to contact the customer.' },
  { icon: '🖼️', title: 'Product images',
    text: 'The images of the cracker boxes shown are for illustrative purposes only.' },
];

const HIGHLIGHTS = [
  { k: 'Tamil Nadu', v: fmt(MIN_ORDER_TN), s: 'Minimum order' },
  { k: 'Other States', v: fmt(MIN_ORDER_OTHER), s: 'Minimum order' },
  { k: 'Tamil Nadu', v: '10–15 days', s: 'Order before Diwali' },
  { k: 'Other States', v: '1 month', s: 'Order before Diwali' },
];

export default function TermsPage() {
  const wa = `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi! I have a question about ordering.')}`;

  return (
    <div className="page-top ip-page">
      {/* Hero */}
      <section className="ip-hero">
        <div className="ip-hero-glow" aria-hidden="true" />
        <div className="ip-wrap ip-hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}>
            <span className="ip-eyebrow">📜 Please read before ordering</span>
            <h1 className="ip-title">Terms &amp; <span>Conditions</span></h1>
            <p className="ip-lead">Simple rules that help us deliver your crackers safely and on time.</p>
          </motion.div>
        </div>
      </section>

      <div className="ip-wrap">
        {/* Quick highlights */}
        <div className="tc-highlights">
          {HIGHLIGHTS.map((h, i) => (
            <motion.div key={i} className={`tc-hl tc-hl-${i}`}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 + i * .06 }}>
              <span className="tc-hl-k">{h.k}</span>
              <span className="tc-hl-v">{h.v}</span>
              <span className="tc-hl-s">{h.s}</span>
            </motion.div>
          ))}
        </div>

        {/* Court notice */}
        <div className="ip-notice">
          <span className="ip-notice-ico" aria-hidden="true">!</span>
          <p>As per the Supreme Court order, online sale of firecrackers is not permitted.
            Please add products and submit your enquiry — we will contact you to confirm your order.</p>
        </div>

        {/* Terms list */}
        <ol className="tc-list">
          {TERMS.map((t, i) => (
            <motion.li key={t.title} className="tc-item"
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }} transition={{ duration: .4 }}>
              <span className="tc-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="tc-icon" aria-hidden="true">{t.icon}</span>
              <div>
                <h2 className="tc-item-title">{t.title}</h2>
                <p className="tc-item-text">{t.text}</p>
              </div>
            </motion.li>
          ))}
        </ol>

        {/* Thank you */}
        <section className="ip-thanks">
          <p className="ip-thanks-emoji" aria-hidden="true">🎆🙏🎇</p>
          <h2>Thank you for choosing and supporting us</h2>
          <p>Have a question about your order? {SHOP_NAME} is happy to help.</p>
          <div className="ip-thanks-actions">
            <a href={`tel:${SHOP_PHONES[0].replace(/\s/g, '')}`} className="ip-btn ip-btn-light">📞 {SHOP_PHONES[0]}</a>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="ip-btn ip-btn-wa">💬 WhatsApp</a>
            {ADMIN_EMAIL && <a href={`mailto:${ADMIN_EMAIL}`} className="ip-btn ip-btn-ghost">✉️ {ADMIN_EMAIL}</a>}
          </div>
          <Link to="/products" className="ip-link">Start shopping →</Link>
        </section>
      </div>
    </div>
  );
}
